import { type FormEvent, useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export interface NewAnchorData {
  url: string;
  domain: string;
  label: string | null;
}

export interface AddAnchorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (anchor: NewAnchorData) => void;
  isSubmitting?: boolean;
}

export function AddAnchorDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}: AddAnchorDialogProps) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    try {
      const normalizedUrl = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
      const parsedUrl = new URL(normalizedUrl);
      if (!parsedUrl.hostname) throw new Error('Missing domain');
      onSubmit({
        url: parsedUrl.toString(),
        domain: parsedUrl.hostname,
        label: label.trim() || null,
      });
    } catch {
      setError('Enter a valid URL, such as https://example.com/customer/123.');
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isSubmitting) {
      setUrl('');
      setLabel('');
      setError(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Add anchor</DialogTitle>
            <DialogDescription>
              Connect another page to this contact so its context is available from that URL.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="new-anchor-url">Page URL</Label>
            <Input
              id="new-anchor-url"
              type="text"
              inputMode="url"
              autoComplete="url"
              autoFocus
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                setError(null);
              }}
              placeholder="https://example.com/customer/123"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'new-anchor-url-error' : undefined}
              required
            />
            {error && (
              <p id="new-anchor-url-error" className="text-xs text-destructive">
                {error}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-anchor-label">Label (optional)</Label>
            <Input
              id="new-anchor-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="e.g. Salesforce contact record"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !url.trim()}>
              {isSubmitting ? 'Adding…' : 'Add anchor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
